import { NextPage } from 'next';
import React from 'react';
import styled from 'styled-components';
import { FdrTextBlock } from '@hotelplan/fdr.regular.basis.fdr-text-block';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import { mapProductFeatureGroupsToProductFeatures } from 'components/domain/fdr-geo/fdr-geo.utils';
import FdrThemeGeoRecommender from 'components/domain/fdr-geo/fdr-theme-geo-recommender';
import { FdrHeroSection } from 'components/domain/fdr-hero-section';
import { FdrPageComponents } from 'components/domain/fdr-page-components';
import {
  isFdrAdventureTravelProduct,
  isFdrHotelProduct,
  isFdrThProduct,
} from 'components/domain/fdr-product/fdr-product.utils';
import { FdrRequestButton } from 'components/domain/fdr-request-button';
import FdrThemeGeoProductRecommender from 'components/domain/fdr-theme-geo/fdr-theme-geo-product-recommender';
import useThemeGeoPageComponents from 'components/domain/fdr-theme-geo/useThemeGeoPageComponents';
import { FdrPageComponentsFragment } from 'schemas/fragment/page-components/fdr-page-components.generated';
import { FdrThemeGeoContentQuery } from 'schemas/query/theme-geo/fdr-theme-geo-content.generated';
import { colors } from 'theme';

export { getServerSideProps } from 'components/page/theme-geo/theme-geo.gssp';

interface IThemeGeoPageProps {
  themeGeoPage: FdrThemeGeoContentQuery['fdrThemeGeoPage'];
  pageComponents: FdrPageComponentsFragment['components'];
  geoStaticPageComponents: FdrPageComponentsFragment['components'];
}

const ThemeGeoContentOuterWrapper = styled.div(
  sx2CssThemeFn({
    backgroundColor: 'redBurgundy',
  })
);

const ThemeGeoContentInnerWrapper = styled.div(
  sx2CssThemeFn({
    maxWidth: ['initial', '818px'],
    margin: 'auto',
    pt: [6, 9],
    pb: [6, 9],
    pl: [3, 0],
    pr: [3, 0],
  })
);

const ThemeGeoTextWrapper = styled(FdrTextBlock)(
  sx2CssThemeFn({
    fontSize: 2,
    color: 'white',
    mb: 6,
  })
);

const ThemeGeoPage: NextPage<IThemeGeoPageProps> = ({ themeGeoPage }) => {
  const components = useThemeGeoPageComponents(
    themeGeoPage.themeId,
    themeGeoPage.geoId
  );

  const productRecommendationComponent = {
    items: themeGeoPage.page?.themeGeoProducts.products
      ?.map(product => {
        if (isFdrThProduct(product)) {
          return {
            ...product,
            image: product.images[0],
            link: product.webMeta.descriptionWebMeta.link,
            features: isFdrHotelProduct(product)
              ? []
              : mapProductFeatureGroupsToProductFeatures(product.featureGroups),
            ...(isFdrAdventureTravelProduct(product) && {
              routePoints: product.route,
              route: product.route?.map(r => r.locationName) || [],
            }),
          };
        }
      })
      .filter(product => product),
    name: themeGeoPage.page?.title,
    total: themeGeoPage.page?.themeGeoProducts.pagination.total || 0,
  };

  return (
    <>
      <FdrHeroSection
        title={themeGeoPage.page?.title}
        text={themeGeoPage.page?.tagLine?.text}
        hero={themeGeoPage.page?.hero}
      />
      <ThemeGeoContentOuterWrapper>
        <ThemeGeoContentInnerWrapper>
          {themeGeoPage.page?.text?.length > 0 && (
            <ThemeGeoTextWrapper text={themeGeoPage.page.text} />
          )}
          <FdrRequestButton
            {...themeGeoPage.page?.requestButton}
            caption={
              themeGeoPage.page?.requestButton?.objectCaption ||
              themeGeoPage.page?.requestButton?.staticCaption
            }
            backgroundColor={colors.redBurgundy}
          />
        </ThemeGeoContentInnerWrapper>
      </ThemeGeoContentOuterWrapper>
      <FdrThemeGeoProductRecommender
        geoId={themeGeoPage.geoId}
        themeId={themeGeoPage.themeId}
        component={productRecommendationComponent}
      />
      <FdrPageComponents components={components} />
      <FdrThemeGeoRecommender
        geoId={themeGeoPage.geoId}
        themeId={themeGeoPage.themeId}
        themeGeoPages={themeGeoPage.page.themeGeoPagesRecommender}
      />
    </>
  );
};

export default ThemeGeoPage;
